import { useState, useEffect, useMemo, useCallback, memo, useRef, lazy, Suspense } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import SEO, { websiteJsonLd, organizationJsonLd, generateFAQJsonLd, getTranslatedFAQs } from '@/components/SEO';
import SwipeableChecklistItem from '@/components/SwipeableChecklistItem';
import GeneratingOverlay from '@/components/GeneratingOverlay';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Sparkles, Copy, Printer, Loader2, Plus, Save, WifiOff, CheckCircle2, Zap, Users } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useTypewriter } from '@/hooks/useTypewriter';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useHaptics } from '@/hooks/useHaptics';
import { useOfflineChecklists, OfflineChecklist } from '@/hooks/useOfflineChecklists';
import { useParallax } from '@/hooks/useParallax';
import { useUserCountry } from '@/hooks/useUserCountry';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { findMatchingProducts, getFeaturedProducts } from '@/data/affiliateProducts';
import type { ProductItem } from '@/components/affiliate/ProductRecommendations';

// Lazy load below-the-fold components to reduce initial bundle size
const ParallaxBackground = lazy(() => import('@/components/ParallaxBackground'));
const PWAInstallPrompt = lazy(() => import('@/components/PWAInstallPrompt'));
const RecentChecklists = lazy(() => import('@/components/RecentChecklists'));
const RecommendedProducts = lazy(() => import('@/components/affiliate/RecommendedProducts'));
const SlideInProductBanner = lazy(() => import('@/components/affiliate/SlideInProductBanner'));
const ItemProductRecommendations = lazy(() => import('@/components/affiliate/ItemProductRecommendations'));
const GuestConversionPrompt = lazy(() => import('@/components/GuestConversionPrompt'));

const MAX_TOPIC_LENGTH = 500;

// Memoized quick suggestion examples - split into personal and business
const PERSONAL_SUGGESTIONS = [
  { emoji: '🏠', label: 'Moving checklist' },
  { emoji: '✈️', label: 'Travel packing' },
  { emoji: '💍', label: 'Wedding planning' },
] as const;

const BUSINESS_SUGGESTIONS = [
  { emoji: '🧾', label: 'Client onboarding SOP' },
  { emoji: '👥', label: 'Employee onboarding' },
  { emoji: '🚀', label: 'Business launch' },
] as const;

// Memoized placeholder examples - B2C and B2B use cases
const PLACEHOLDER_EXAMPLES = [
  "Hosting a dinner party for 10 people...",
  "Onboarding a new employee...",
  "Launching a new product...",
  "Preparing a client onboarding process...",
  "Planning a wedding for 150 guests...",
  "Moving to a new apartment next month...",
  "Setting up a new team workflow...",
  "Preparing for a job interview..."
] as const;

interface GeneratedItem {
  text: string;
  note?: string;
  products?: ProductItem[];
}
const Index = () => {
  const {
    t
  } = useTranslation();
  const {
    toast
  } = useToast();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const {
    lightTap,
    successFeedback,
    selectionTap
  } = useHaptics();
  const { isPremium, isTeamMember } = useSubscription();
  const { countryCode } = useUserCountry();
  const { offset: heroOffset, opacity: heroOpacity } = useParallax({ speed: 0.3 });
  const { offset: subtitleOffset } = useParallax({ speed: 0.15 });
  const {
    saveChecklist: saveOffline,
    checklists: offlineChecklists,
    deleteChecklist: deleteOffline
  } = useOfflineChecklists();
  const [topic, setTopic] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedTitle, setGeneratedTitle] = useState('');
  const [generatedItems, setGeneratedItems] = useState<GeneratedItem[]>([]);
  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());
  const [currentOfflineId, setCurrentOfflineId] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showSlideInBanner, setShowSlideInBanner] = useState(false);
  const [slideInProduct, setSlideInProduct] = useState<ReturnType<typeof findMatchingProducts>[0] | null>(null);
  // Server-side quota tracking
  const [quota, setQuota] = useState<{ used: number; limit: number; remaining: number; tier: string } | null>(null);

  // Handle focus param from /generate redirect
  useEffect(() => {
    if (searchParams.get('focus') === 'generator') {
      textareaRef.current?.focus();
      // Clean up the URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Use memoized placeholder examples
  const { displayedText: displayedPlaceholder } = useTypewriter({
    examples: PLACEHOLDER_EXAMPLES as unknown as string[],
    enabled: !topic,
  });
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Auto-save pending project after guest→account conversion
  useEffect(() => {
    if (!user) return;
    const pending = localStorage.getItem('nexihow_pending_project');
    if (!pending) return;
    
    try {
      const project = JSON.parse(pending);
      if (project.title && project.items?.length > 0) {
        supabase.from('saved_checklists').insert([{
          user_id: user.id,
          title: project.title,
          items: project.items as unknown as Json[],
        }]).then(({ error }) => {
          if (!error) {
            localStorage.removeItem('nexihow_pending_project');
            toast({
              title: 'Project saved!',
              description: `"${project.title}" has been saved to your account.`,
            });
          }
        });
      }
    } catch {
      localStorage.removeItem('nexihow_pending_project');
    }
  }, [user]);

  // Fetch quota on mount
  useEffect(() => {
    const fetchQuota = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('check-quota');
        if (!error && data?.quota) {
          setQuota(data.quota);
        }
      } catch (err) {
        console.error('Failed to fetch quota:', err);
      }
    };
    fetchQuota();
  }, [user]);
  
  useEffect(() => {
    if (generatedItems.length > 0 && generatedTitle) {
      const id = saveOffline(generatedTitle, generatedItems, Array.from(checkedItems), currentOfflineId || undefined);
      if (!currentOfflineId) setCurrentOfflineId(id);
    }
  }, [generatedItems, generatedTitle, checkedItems]);
  
  // Default quota for anonymous users (before first generation)
  const FREE_LIMIT = 3;
  const freeRemaining = quota ? quota.remaining : FREE_LIMIT;
  const freeUsed = quota ? quota.used : 0;
  const quotaLimit = quota ? quota.limit : FREE_LIMIT;
  
  const translatedFAQs = getTranslatedFAQs(t);
  const combinedJsonLd = [websiteJsonLd, organizationJsonLd, generateFAQJsonLd(translatedFAQs)];
  const handleGenerate = async () => {
    // Only block if we have quota info and it shows no remaining (server will enforce anyway)
    if (!user && quota && quota.remaining <= 0) {
      toast({
        title: t('generate.loginRequired'),
        description: 'Create a free account for more generations!'
      });
      navigate('/auth');
      return;
    }
    if (!topic.trim()) {
      toast({
        title: t('common.error'),
        description: t('generate.inputLabel'),
        variant: 'destructive'
      });
      return;
    }
    if (topic.length > MAX_TOPIC_LENGTH) {
      toast({
        title: t('common.error'),
        description: t('generate.topicTooLong', {
          max: MAX_TOPIC_LENGTH
        }),
        variant: 'destructive'
      });
      return;
    }
    setIsGenerating(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('generate-checklist', {
        body: {
          topic: topic.trim(),
          countryCode
        }
      });
      if (error) {
        if (error.message?.includes('401') || error.message?.includes('Authentication')) {
          toast({
            title: t('generate.loginRequired'),
            description: t('generate.loginRequiredDesc'),
            variant: 'destructive'
          });
          navigate('/auth');
          return;
        }
        throw new Error(error.message || 'Failed to generate checklist');
      }
      if (data.error) {
        if (data.requiresAuth || data.limitReached) {
          // Update quota from error response
          if (data.quota) {
            setQuota(data.quota);
          }
          toast({
            title: data.requiresAuth ? t('generate.loginRequired') : t('common.error'),
            description: data.error,
            variant: 'destructive'
          });
          if (data.requiresAuth) navigate('/auth');
          return;
        }
        throw new Error(data.error);
      }
      
      // Update quota from successful response
      if (data.quota) {
        setQuota(data.quota);
      }
      setCurrentOfflineId(null);
      const newTitle = data.title || `${topic.charAt(0).toUpperCase() + topic.slice(1)} Checklist`;
      setGeneratedTitle(newTitle);
      setGeneratedItems(data.items || []);
      setCheckedItems(new Set());
      
      // Find a matching product for the slide-in banner
      const matchedProducts = findMatchingProducts(`${topic} ${newTitle}`, 1);
      if (matchedProducts.length > 0) {
        setSlideInProduct(matchedProducts[0]);
        setShowSlideInBanner(true);
      } else {
        const featured = getFeaturedProducts(1);
        if (featured.length > 0) {
          setSlideInProduct(featured[0]);
          setShowSlideInBanner(true);
        }
      }
      
      successFeedback();
      toast({
        title: t('common.success'),
        description: t('generate.generated', {
          count: data.items?.length || 0
        })
      });
    } catch (error) {
      console.error('Generation error:', error);
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };
  const toggleItem = (index: number) => {
    selectionTap();
    const newChecked = new Set(checkedItems);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setCheckedItems(newChecked);
  };
  const handleCopy = () => {
    lightTap();
    const text = `${generatedTitle}\n\n${generatedItems.map(item => `☐ ${item.text}${item.note ? ` (${item.note})` : ''}`).join('\n')}`;
    navigator.clipboard.writeText(text);
    toast({
      title: t('common.success'),
      description: t('checklist.copy')
    });
  };
  const handlePrint = () => window.print();
  const addItem = () => setGeneratedItems([...generatedItems, {
    text: ''
  }]);
  const updateItem = (index: number, text: string) => {
    const newItems = [...generatedItems];
    newItems[index] = {
      ...newItems[index],
      text
    };
    setGeneratedItems(newItems);
  };
  const removeItem = (index: number) => {
    setGeneratedItems(generatedItems.filter((_, i) => i !== index));
    const newChecked = new Set<number>();
    checkedItems.forEach(i => {
      if (i < index) newChecked.add(i);else if (i > index) newChecked.add(i - 1);
    });
    setCheckedItems(newChecked);
  };
  const loadSavedChecklist = (checklist: OfflineChecklist) => {
    setGeneratedTitle(checklist.title);
    setGeneratedItems(checklist.items);
    setCheckedItems(new Set(checklist.checkedItems));
    setCurrentOfflineId(checklist.id);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleSave = async () => {
    if (!user) {
      // Preserve project for post-signup auto-save
      localStorage.setItem('nexihow_pending_project', JSON.stringify({
        title: generatedTitle,
        items: generatedItems,
      }));
      toast({
        title: 'Create a free account to save this project',
        description: 'Your work will be preserved after sign up.',
      });
      navigate('/auth');
      return;
    }
    setIsSaving(true);
    try {
      const {
        error
      } = await supabase.from('saved_checklists').insert([{
        user_id: user.id,
        title: generatedTitle,
        items: generatedItems as unknown as Json[]
      }]);
      if (error) throw error;
      toast({
        title: t('checklist.saved'),
        description: t('generate.saved')
      });
      navigate('/saved');
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: t('common.error'),
        description: 'Failed to save checklist. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };
  return <Layout>
      <SEO title="AI Checklist Generator - Create Plans Instantly" description="Generate perfect checklists for any task with AI. Moving, travel, weddings, business - create comprehensive checklists in seconds. Free to try." canonical="/" jsonLd={combinedJsonLd} />
      
      {/* Generating overlay */}
      <GeneratingOverlay isVisible={isGenerating} />
      
      {/* Full-page visual effects with scroll parallax */}
      <Suspense fallback={null}>
        <ParallaxBackground />
      </Suspense>
      
      {/* Hero Section - AI-first, ChatGPT-inspired */}
      <section className="flex flex-col justify-center relative">
        <div className="container mx-auto px-4 py-10 sm:py-12 relative z-10">
          <div className="max-w-3xl mx-auto">
            {/* Centered headline with parallax */}
            <div 
              className="text-center mb-12 sm:mb-16 will-change-transform"
              style={{ 
                transform: `translateY(${heroOffset}px)`,
                opacity: heroOpacity,
                transition: 'opacity 0.1s ease-out'
              }}
            >
              <h1 className="font-apple text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-5 leading-[1.05] tracking-tight">
                <span className="font-semibold animate-fade-in-1">What can I help you</span>
                <br />
                <span className="text-gradient font-semibold animate-fade-in-2">organize today?</span>
              </h1>
              <p 
                className="text-lg sm:text-xl text-muted-foreground/80 max-w-xl mx-auto animate-fade-in-3"
                style={{ transform: `translateY(${subtitleOffset * 0.5}px)` }}
              >
                Turn any task or process into a clear, structured plan in seconds.
              </p>
            </div>

            {/* AI-style input with enhanced glow - the primary visual focus */}
            <div className="animate-fade-in-4 relative">
              {/* Ambient glow behind input */}
              <div className="absolute -inset-4 sm:-inset-6 bg-gradient-to-r from-highlight/20 via-primary/10 to-purple-500/20 rounded-[2rem] blur-2xl opacity-60 animate-pulse-subtle" />
              <div className="absolute -inset-2 sm:-inset-3 bg-gradient-to-br from-highlight/10 to-purple-500/10 rounded-[1.75rem] blur-xl opacity-40" />
              
              <div className="relative glass-card overflow-hidden shadow-2xl shadow-highlight/15 ring-1 ring-white/10 dark:ring-white/5 transition-all duration-500 hover:shadow-highlight/25 hover:ring-white/20 focus-within:shadow-highlight/30 focus-within:ring-highlight/30 focus-within:scale-[1.01]">
                <Textarea 
                  ref={textareaRef}
                  placeholder={displayedPlaceholder || "Describe what you need a checklist for..."} 
                  value={topic} 
                  onChange={e => setTopic(e.target.value.slice(0, MAX_TOPIC_LENGTH + 50))} 
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey && topic.trim() && !isGenerating) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }} 
                  maxLength={MAX_TOPIC_LENGTH + 50} 
                  className="w-full min-h-[120px] sm:min-h-[140px] resize-none border-0 bg-transparent text-base sm:text-lg p-5 sm:p-6 pb-16 placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:ring-offset-0" 
                  rows={4} 
                />
                
                {/* Floating action bar */}
                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 sm:px-5 py-3">
                  <div className="text-xs text-muted-foreground flex items-center gap-3">
                    {topic.length > 0 && (
                      <span className={topic.length > MAX_TOPIC_LENGTH ? 'text-destructive font-medium' : ''}>
                        {topic.length}/{MAX_TOPIC_LENGTH}
                      </span>
                    )}
                    {!user && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{freeRemaining} free left</span>
                        <span className="text-muted-foreground/50">·</span>
                        <Link to="/auth" className="text-foreground hover:underline font-medium">Sign up</Link>
                      </div>
                    )}
                    {user && (isPremium || isTeamMember) && (
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 gap-1">
                        <Zap className="h-3 w-3" />
                        Priority Processing
                      </Badge>
                    )}
                  </div>
                  <Button 
                    onClick={() => { lightTap(); handleGenerate(); }} 
                    disabled={isGenerating || !topic.trim() || topic.length > MAX_TOPIC_LENGTH || (!user && quota && quota.remaining <= 0)} 
                    size="icon"
                    className="h-10 w-10 rounded-full"
                    aria-label={isGenerating ? "Generating checklist" : "Generate checklist"}
                  >
                    {isGenerating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Suggestion chips - lightweight visual guidance */}
            <div className="mt-8 sm:mt-10 animate-fade-in-5 space-y-5">
              {/* Personal use suggestions */}
              <div className="space-y-2.5">
                <p className="text-[11px] text-muted-foreground/50 text-center uppercase tracking-widest font-medium">For personal use</p>
                <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5">
                  {PERSONAL_SUGGESTIONS.map(example => (
                    <button 
                      key={example.label} 
                      onClick={() => { selectionTap(); setTopic(example.label); }} 
                      disabled={!user && quota && quota.remaining <= 0} 
                      className="flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-border/50 bg-background/50 backdrop-blur-sm hover:bg-secondary/80 hover:border-border transition-all disabled:opacity-50 active:scale-[0.97]"
                    >
                      <span className="opacity-80">{example.emoji}</span>
                      <span className="text-foreground/80">{example.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Teams & business suggestions */}
              <div className="space-y-2.5">
                <p className="text-[11px] text-muted-foreground/50 text-center uppercase tracking-widest font-medium">For teams & businesses</p>
                <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5">
                  {BUSINESS_SUGGESTIONS.map(example => (
                    <button 
                      key={example.label} 
                      onClick={() => { selectionTap(); setTopic(example.label); }} 
                      disabled={!user && quota && quota.remaining <= 0} 
                      className="flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-border/50 bg-background/50 backdrop-blur-sm hover:bg-secondary/80 hover:border-border transition-all disabled:opacity-50 active:scale-[0.97]"
                    >
                      <span className="opacity-80">{example.emoji}</span>
                      <span className="text-foreground/80">{example.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Credibility line - improved visibility */}
              <p className="text-xs text-muted-foreground/60 text-center pt-4 font-medium tracking-wide">
                Used by individuals and teams to organize work and life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Affiliate recommendations only show after generation - tailored to user's query */}

      {/* Recent Checklists */}
      {generatedItems.length === 0 && offlineChecklists.length > 0 && (
        <Suspense fallback={null}>
          <RecentChecklists checklists={offlineChecklists} onSelect={loadSavedChecklist} onDelete={deleteOffline} />
        </Suspense>
      )}

      {/* Generated Results */}
      {generatedItems.length > 0 && <section className="container mx-auto px-4 py-8 sm:py-12" aria-labelledby="results-heading">
          <div className="max-w-2xl mx-auto">
            <h2 id="results-heading" className="sr-only">Your generated checklist</h2>
            <Card className="shadow-lg border-primary/20">
              <CardHeader className="bg-primary/5 rounded-t-lg px-3 sm:px-6 py-3 sm:py-4">
                <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3">
                  <CardTitle className="text-base sm:text-xl flex items-center gap-1.5 sm:gap-2">
                    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    <span className="truncate">{generatedTitle}</span>
                  </CardTitle>
                  <div className="flex gap-1.5 sm:gap-2">
                    <Button variant="outline" size="sm" onClick={handleCopy} className="h-8 px-2 sm:px-3 text-xs sm:text-sm">
                      <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" /> <span className="hidden sm:inline">Copy</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={handlePrint} className="h-8 px-2 sm:px-3 text-xs sm:text-sm hidden sm:flex">
                      <Printer className="h-4 w-4 mr-1" /> Print
                    </Button>
                    {user ? (
                      <Button size="sm" onClick={handleSave} disabled={isSaving} className="h-8 px-2 sm:px-3 text-xs sm:text-sm">
                        {isSaving ? <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" /> : <Save className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />}
                        <span className="hidden sm:inline">Save</span>
                      </Button>
                    ) : (
                      <Button size="sm" asChild className="h-8 px-2 sm:px-3 text-xs sm:text-sm">
                        <Link to="/auth">
                          <Save className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />
                          <span className="hidden sm:inline">Sign up to save</span>
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
                <div className="space-y-1.5 sm:space-y-2">
                  {generatedItems.map((item, index) => (
                    <div key={index}>
                      <SwipeableChecklistItem 
                        text={item.text} 
                        note={item.note} 
                        checked={checkedItems.has(index)} 
                        onToggle={() => toggleItem(index)} 
                        onUpdate={text => updateItem(index, text)} 
                        onDelete={() => removeItem(index)} 
                      />
                      {item.products && item.products.length > 0 && !checkedItems.has(index) && (
                        <Suspense fallback={null}>
                          <ItemProductRecommendations 
                            products={item.products}
                            sourceCategory={generatedTitle.toLowerCase().includes('travel') ? 'travel' : 
                                           generatedTitle.toLowerCase().includes('home') ? 'home' : 'general'}
                          />
                        </Suspense>
                      )}
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="w-full mt-3 sm:mt-4 text-sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-2" /> {t('checklist.addItem')}
                </Button>
              </CardContent>
            </Card>

            {/* Tailored Affiliate Products - Based on User's Query */}
            {generatedItems.length > 0 && (
              <div className="mt-6">
                <Suspense fallback={null}>
                  <RecommendedProducts 
                    searchTerm={topic || generatedTitle}
                    category={generatedTitle.toLowerCase().includes('travel') ? 'travel' : 
                             generatedTitle.toLowerCase().includes('home') || generatedTitle.toLowerCase().includes('moving') ? 'home' :
                             generatedTitle.toLowerCase().includes('wedding') ? 'wedding' :
                             generatedTitle.toLowerCase().includes('baby') ? 'baby' :
                             generatedTitle.toLowerCase().includes('fitness') || generatedTitle.toLowerCase().includes('gym') ? 'fitness' :
                             generatedTitle.toLowerCase().includes('office') || generatedTitle.toLowerCase().includes('business') ? 'office' :
                             'general'}
                    limit={3}
                    title="Recommended for Your Checklist"
                  />
                </Suspense>
              </div>
            )}
          </div>
        </section>}


      {/* Minimal FAQ Section - Just key questions */}
      <section className="py-6 sm:py-10 border-t border-border/30" aria-labelledby="faq-heading">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 id="faq-heading" className="sr-only">Frequently asked questions</h2>
          <div className="space-y-6 text-center">
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-4 text-sm">
                <span className="text-muted-foreground">Is Nexihow free?</span>
                <span className="font-medium text-foreground">Yes.</span>
              </div>
              <div className="flex items-center justify-center gap-4 text-sm">
                <span className="text-muted-foreground">Can teams use it?</span>
                <span className="font-medium text-foreground">Yes.</span>
              </div>
              <div className="flex items-center justify-center gap-4 text-sm">
                <span className="text-muted-foreground">Do I need to install anything?</span>
                <span className="font-medium text-foreground">No.</span>
              </div>
            </div>
            <Link 
              to="/faq" 
              className="inline-flex text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              More questions →
            </Link>
          </div>
        </div>
      </section>

      <Suspense fallback={null}>
        <PWAInstallPrompt />
      </Suspense>

      {/* Guest conversion prompt - shown to unauthenticated users with unsaved work */}
      {!user && generatedItems.length > 0 && (
        <Suspense fallback={null}>
          <GuestConversionPrompt
            hasUnsavedProject={generatedItems.length > 0}
            projectTitle={generatedTitle}
          />
        </Suspense>
      )}

      {/* Slide-in Product Banner - appears after AI generation */}
      {slideInProduct && (
        <Suspense fallback={null}>
          <SlideInProductBanner
            product={slideInProduct}
            show={showSlideInBanner}
            onDismiss={() => setShowSlideInBanner(false)}
            sourceCategory={generatedTitle.toLowerCase().includes('travel') ? 'travel' : 
                           generatedTitle.toLowerCase().includes('home') ? 'home' : 
                           generatedTitle.toLowerCase().includes('wedding') ? 'wedding' : 'general'}
            delay={2000}
          />
        </Suspense>
      )}

      {!isOnline && <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center gap-1.5 sm:gap-2 bg-amber-500/90 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium shadow-lg backdrop-blur-sm">
            <WifiOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Offline - changes saved locally
          </div>
        </div>}
    </Layout>;
};
export default Index;